import React from 'react'
import PropTypes from 'prop-types'
import {Button} from '@/components/cta'
import {coreButtonInputs, buttonFunctions, coreButtonPropTypes} from './cta.helper'
import {useDetermineRouteTypeAndUrl} from 'hec/hooks/useDetermineRouteTypeAndUrl'
import useFetchPageDataForCta from './useFetchPageDataForCta'

const BuilderButton = ({
    disabled,
    variant,
    openInNewTab,
    children,
    clickFunction = buttonFunctions.linkToPage,
    scrollTargetId,
    attributes,
    isInternalLink,
    isRevShare,
    pageReference,
    queryParams,
    externalLinkReference,
    dataTestId
}) => {
    const {className: classNameAttribute, ...restAttributes} = attributes || {}

    const {
        pageRef,
        externalLinkRef,
        isLoading: isPageDataLoading,
        error: pageDataError
    } = useFetchPageDataForCta({
        isInternalLink,
        pageReference,
        externalLinkReference
    })

    const {routeType, url} = useDetermineRouteTypeAndUrl({
        clickFunction,
        isInternalLink,
        pageReference: pageRef,
        externalLinkReference: externalLinkRef
    })

    if (isPageDataLoading) {
        return null
    }

    if (pageDataError) {
        console.error('Error fetching page data for CTA: ', pageDataError)
        // Return a fallback button instead of null to prevent layout issues
        return (
            <Button
                ctaType="button"
                disabled={true}
                variant={variant}
                className={classNameAttribute}
                {...restAttributes}
                data-testid={dataTestId || 'button-Button-error'}
            >
                {children}
            </Button>
        )
    }

    return (
        <Button
            ctaType="button"
            disabled={disabled}
            variant={variant}
            openInNewTab={openInNewTab}
            clickFunction={clickFunction}
            scrollTargetId={scrollTargetId}
            isRevShare={isRevShare}
            className={classNameAttribute}
            routeType={routeType}
            url={url}
            queryParams={queryParams}
            dataTestId={dataTestId}
            {...restAttributes}
            data-testid={dataTestId || 'button-Button'}
        >
            {children}
        </Button>
    )
}

BuilderButton.propTypes = {
    className: PropTypes.string,
    variant: PropTypes.string,
    disabled: PropTypes.bool,
    ...coreButtonPropTypes
}

export const ButtonDefinition = {
    component: BuilderButton,
    name: 'Core:Button',
    override: true,
    noWrap: true,
    canHaveChildren: true,
    defaultChildren: [
        {
            '@type': '@builder.io/sdk:Element',
            component: {
                name: 'Text',
                options: {text: 'Click me'}
            }
        }
    ],
    childRequirements: {
        message: 'You can only put Text, Heading and Icons into Button component',
        query: {
            'component.name': {$in: ['Text', 'IconPicker', 'Heading']}
        }
    },
    inputs: [
        {
            name: 'variant',
            type: 'string',
            enum: ['primary', 'secondary'],
            defaultValue: 'primary'
        },
        {
            name: 'disabled',
            type: 'boolean',
            defaultValue: false
        },
        ...coreButtonInputs
    ]
}
